import { useState, type FormEvent } from 'react'

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorText,
  Icon,
  Input,
  PageSpinner,
  Sheet,
  useToast,
} from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDate } from '@/lib/format'
import { todayIso } from '@/lib/periods'

import {
  useAddSupplement,
  useDeleteSupplement,
  useSetIntake,
  useSupplements,
  type Supplement,
} from './api'
import { adherencePercent, fieldError, otherError } from './logic'
import { DeleteButton, TabToolbar } from './parts'

function SupplementForm({ onDone }: { onDone: () => void }) {
  const toast = useToast()
  const add = useAddSupplement()
  const [name, setName] = useState('')
  const [dose, setDose] = useState('')
  const [frequency, setFrequency] = useState('')
  const [startedOn, setStartedOn] = useState(todayIso())
  const [endedOn, setEndedOn] = useState('')
  const [missing, setMissing] = useState(false)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setMissing(true)
      return
    }
    add.mutate(
      {
        name: name.trim(),
        dose: dose.trim() || null,
        frequency: frequency.trim() || null,
        started_on: startedOn || null,
        ended_on: endedOn || null,
      },
      {
        onSuccess: () => {
          toast.show({ message: 'Курс добавлен', tone: 'accent' })
          onDone()
        },
      },
    )
  }

  return (
    <form onSubmit={submit} className="grid gap-4" noValidate>
      <Input
        label="Название"
        value={name}
        placeholder="Например, магний"
        onChange={(e) => {
          setName(e.target.value)
          setMissing(false)
        }}
        error={missing ? 'Напиши название' : fieldError(add.error, 'name')}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Дозировка" value={dose} placeholder="400 мг" onChange={(e) => setDose(e.target.value)} />
        <Input
          label="Как часто"
          value={frequency}
          placeholder="1 раз в день"
          onChange={(e) => setFrequency(e.target.value)}
        />
        <Input label="Начало" type="date" value={startedOn} onChange={(e) => setStartedOn(e.target.value)} />
        <Input
          label="Окончание"
          type="date"
          value={endedOn}
          onChange={(e) => setEndedOn(e.target.value)}
          error={fieldError(add.error, 'ended_on')}
          hint="Пусто — без конца"
        />
      </div>
      <ErrorText error={otherError(add.error, 'name', 'ended_on')} />
      <Button type="submit" variant="primary" loading={add.isPending}>
        Добавить
      </Button>
    </form>
  )
}

function IntakeButton({ item, day }: { item: Supplement; day: string }) {
  const set = useSetIntake()
  return (
    <button
      type="button"
      role="switch"
      aria-checked={item.taken}
      aria-label={`${item.taken ? 'Принято' : 'Принял'}: ${item.name}`}
      disabled={set.isPending}
      onClick={() => set.mutate({ id: item.id, day, taken: !item.taken })}
      className={cn(
        'flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
        item.taken ? 'border-accent bg-accent text-accent-fg' : 'border-border bg-surface text-muted hover:bg-surface-2',
      )}
    >
      <Icon name="check" size={22} />
    </button>
  )
}

function SupplementRow({ item, day }: { item: Supplement; day: string }) {
  const remove = useDeleteSupplement()
  const toast = useToast()
  const detail = [item.dose, item.frequency].filter(Boolean).join(' · ')
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      {item.active ? <IntakeButton item={item} day={day} /> : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-fg">{item.name}</span>
          {item.active ? (
            <Badge tone="accent">{adherencePercent(item.adherence_30d)}% за 30 дней</Badge>
          ) : (
            <Badge>курс завершён</Badge>
          )}
        </div>
        <div className="text-xs text-muted">
          {detail ? `${detail} · ` : ''}
          {item.started_on ? `с ${formatDate(item.started_on)}` : 'без даты начала'}
          {item.ended_on ? ` по ${formatDate(item.ended_on)}` : ''}
        </div>
      </div>
      <DeleteButton
        what={item.name}
        pending={remove.isPending}
        error={remove.error}
        onConfirm={() => remove.mutateAsync(item.id).then(() => toast.show({ message: 'Курс удалён' }))}
      />
    </li>
  )
}

export function SupplementsPage() {
  const day = todayIso()
  const query = useSupplements(day)
  const [open, setOpen] = useState(false)

  if (query.isPending) return <PageSpinner />
  if (query.isError) return <ErrorText error={query.error} />

  const active = query.data.filter((s) => s.active)
  const finished = query.data.filter((s) => !s.active)
  const addButton = (
    <Button variant="primary" icon="plus" onClick={() => setOpen(true)}>
      Добавить добавку
    </Button>
  )

  return (
    <div>
      <TabToolbar
        description="Отметь одним тапом, что принял сегодня. Процент — доля дней с приёмом за 30 дней."
        action={query.data.length ? addButton : null}
      />
      {query.data.length === 0 ? (
        <Card>
          <EmptyState
            icon="check"
            title="Добавок и лекарств пока нет"
            description="Заведи первый курс: название, дозировка, как часто принимать и даты."
            action={addButton}
          />
        </Card>
      ) : (
        <div className="grid gap-4">
          <Card title="Сегодня" flush>
            {active.length ? (
              <ul className="divide-y divide-border">
                {active.map((s) => (
                  <SupplementRow key={s.id} item={s} day={day} />
                ))}
              </ul>
            ) : (
              <EmptyState icon="check" title="Активных курсов нет" description="Добавь новый курс или продли прежний." />
            )}
          </Card>
          {finished.length ? (
            <Card title="Завершённые и будущие курсы" flush>
              <ul className="divide-y divide-border">
                {finished.map((s) => (
                  <SupplementRow key={s.id} item={s} day={day} />
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      )}
      <Sheet open={open} onClose={() => setOpen(false)} title="Добавка или лекарство">
        <SupplementForm onDone={() => setOpen(false)} />
      </Sheet>
    </div>
  )
}
